/**
 * @file ConsentTextsEditor.tsx
 * @description Форма новой редакции текстов согласия (phase19.16, Р3г;
 * uiux-r3g-consent-texts §3–§5): шапка с «Сохранить черновик» и
 * «Опубликовать редакцию N», вкладки с полями, живой предпросмотр
 * (закреплён справа с 1280, колонка не уже 400 px; уже — под формой), окно публикации, сводка
 * ошибок, конфликт 409 и «Правки не сохранены» при уходе.
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import { ArrowDown, Save } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useAuth } from "../../../auth/hooks";
import { useConsentTextsEditor } from "../../../hooks/platform/useConsentTextsEditor";
import { isBannerTexts, type ConsentEditStart } from "../../../hooks/platform/useConsentTextsSource";
import type { ConsentTextRevision } from "../../../lib/api-consent-texts";
import { previewCategories, previewPlaceholderValues } from "../../../lib/platform/consent-preview";
import { CONSENT_TEXT_TABS, consentFieldDomId, consentTabOf, firstTabOf, type ConsentTextsTab } from "../../../lib/platform/consent-texts-fields";
import { Button } from "../../ui/button";
import { CabinetPageHeader } from "../../ui/CabinetPageHeader";
import { useNotify } from "../../ui/NotifyProvider";
import { ConsentConflictNotice } from "./ConsentConflictNotice";
import { ConsentDiffLine } from "./ConsentDiffLine";
import { ConsentEditTabs } from "./ConsentEditTabs";
import { ConsentIssuesSummary } from "./ConsentIssuesSummary";
import { ConsentLeaveGuard } from "./ConsentLeaveGuard";
import { ConsentTextsPreview } from "./ConsentTextsPreview";
import { PublishConsentTextsDialog } from "./PublishConsentTextsDialog";

const LIST_PATH = "/platform/consent-texts";

interface Props {
  kind: ConsentTextKind;
  current: ConsentTextRevision;
  start: ConsentEditStart;
  onReload: () => void;
}

export function ConsentTextsEditor({ kind, current, start, onReload }: Props) {
  const navigate = useNavigate();
  const notify = useNotify();
  const me = useAuth().me;
  const authorName = me?.user.displayName || me?.user.email || "Вы";
  const editor = useConsentTextsEditor(kind, current, start, authorName);
  const [tab, setTab] = useState<ConsentTextsTab>(firstTabOf(kind));
  const [showDiff, setShowDiff] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const left = useRef(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  // Номер — по самой свежей известной редакции: у старого черновика он вырос (ревью М4).
  const number = Math.max(editor.expectedCurrent, current.number, editor.conflict?.revision?.number ?? 0) + 1;

  function onTab(next: ConsentTextsTab) {
    const meta = CONSENT_TEXT_TABS.find((t) => t.value === next);
    if (meta && meta.kind !== kind) navigate(`${LIST_PATH}/new?kind=${meta.kind}`);
    else setTab(next);
  }

  function goToField(path: string) {
    setTab(consentTabOf(kind, path));
    requestAnimationFrame(() => document.getElementById(consentFieldDomId(path))?.focus());
  }

  function saveDraft(): boolean {
    const ok = editor.saveDraft();
    if (ok) notify.success("Черновик сохранён в этом браузере");
    else notify.error("Не удалось сохранить черновик: хранилище браузера недоступно");
    return ok;
  }

  function onPublishClick() {
    if (editor.errors.size > 0) {
      setShowSummary(true);
      setTab(consentTabOf(kind, [...editor.errors.keys()][0] ?? ""));
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }
    if (editor.liveChanged.length === 0) {
      notify.info(`Тексты совпадают с действующей ред. ${editor.expectedCurrent} — публиковать нечего`);
      return;
    }
    setPublishOpen(true);
  }

  async function publish(note: string) {
    const result = await editor.publish(note);
    if (result.ok) {
      left.current = true;
      setPublishOpen(false);
      notify.success(
        kind === "BANNER"
          ? `Редакция ${result.number} опубликована. Магазины получат подсказку «Обновить» в кабинете`
          : `Редакция ${result.number} окна о риске опубликована`,
      );
      navigate(LIST_PATH);
      return;
    }
    if (result.reason === "failed") return;
    setPublishOpen(false);
    if (result.reason === "invalid") {
      setShowSummary(true);
      requestAnimationFrame(() => summaryRef.current?.focus());
    }
  }

  /** «Обновить» при конфликте: правки уже в черновике — открываем его (ревью М4). */
  function reloadFromDraft() {
    // Правки сохранены черновиком при 409 — окно ухода не нужно.
    navigate(`${LIST_PATH}/new?kind=${kind}&draft=1`, { replace: true });
    onReload();
  }

  const texts = editor.texts;
  const hint = isBannerTexts(texts) ? placeholderHintOf(texts) : undefined;
  const publishLabel = `Опубликовать редакцию ${number}`;

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={`Новая редакция ${number}`}
        subtitle={`${kind === "BANNER" ? "Баннер cookie" : "Окно о риске"} · основа: ред. ${editor.base.number}`}
        back={{ to: LIST_PATH, label: "К текстам согласия" }}
        secondary={[{ key: "save", label: "Сохранить черновик", icon: Save, run: () => void saveDraft(), disabled: !editor.dirty }]}
        action={
          <Button variant="brand" loading={editor.publishing} onClick={onPublishClick}>
            {publishLabel}
          </Button>
        }
        mobileAction={
          <div className="flex gap-2">
            <Button variant="outline" className="min-h-12 flex-1" onClick={() => document.getElementById("consent-preview")?.scrollIntoView()}>
              <ArrowDown className="size-4" aria-hidden />
              Предпросмотр
            </Button>
            <Button variant="brand" className="min-h-12 flex-1" loading={editor.publishing} onClick={onPublishClick}>
              Опубликовать {number}
            </Button>
          </div>
        }
      />
      {editor.conflict ? (
        <ConsentConflictNotice conflict={editor.conflict} onCompare={editor.compareWithConflict} onRestart={editor.restartFromConflict} onReload={reloadFromDraft} />
      ) : null}
      {showSummary && editor.errors.size > 0 ? (
        <ConsentIssuesSummary ref={summaryRef} kind={kind} errors={editor.errors} onGo={goToField} />
      ) : null}
      {/* Две колонки — с 1280: форма до 640, предпросмотр не уже 400 (360 + отступы); уже — друг под другом. */}
      <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,40rem)_minmax(25rem,1fr)]">
        <ConsentEditTabs
          kind={kind}
          tab={tab}
          onTab={onTab}
          texts={texts}
          onChange={editor.setField}
          errors={editor.errors}
          warnings={editor.warnings}
          highlighted={showDiff ? new Set(editor.changed.map((c) => c.field)) : new Set()}
          baseNumber={editor.base.number}
          placeholderHint={hint}
        />
        <div id="consent-preview" className="min-w-0 scroll-mt-24 xl:sticky xl:top-4 xl:self-start">
          <ConsentTextsPreview
            kind={kind}
            texts={texts}
            footer={<ConsentDiffLine baseNumber={editor.base.number} count={editor.changed.length} shown={showDiff} onToggle={() => setShowDiff((v) => !v)} />}
          />
        </div>
      </div>
      <PublishConsentTextsDialog
        open={publishOpen}
        onOpenChange={(open) => {
          setPublishOpen(open);
          if (!open) editor.clearPublishError();
        }}
        kind={kind}
        number={number}
        baseNumber={editor.base.number}
        changed={editor.changed}
        affected={editor.liveChanged}
        shops={current.shops}
        publishing={editor.publishing}
        error={editor.publishError}
        onPublish={(note) => void publish(note)}
      />
      <ConsentLeaveGuard when={editor.dirty} onSave={saveDraft} skip={() => left.current} />
    </div>
  );
}

/** «{categories} → «аналитики и рекламы», {services} → «Яндекс Метрика, VK»». */
function placeholderHintOf(texts: Parameters<typeof previewPlaceholderValues>[0]): string {
  const values = previewPlaceholderValues(texts, previewCategories("metrika_vk"));
  return `{categories} → «${values.categories}», {services} → «${values.services}»`;
}
